/// <amd-module name="Wit/WorkItemModel/RuleEngine/RuleEngine" />
import { FieldRuleType } from "Wit/WorkItemDataManager/Contracts";
import { IEvalStateValue, IFieldRule, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { compareFieldValues } from "../WorkItem/FieldUtils";
import { RuleEvaluator } from "./RuleEvaluator";

export class RuleEngine {
    private fieldRules: { [fieldId: number]: FieldRuleType[] };
    private workItem: IWorkItem;

    constructor(workItem: IWorkItem, fieldRules: IFieldRule[]) {
        this.workItem = workItem;
        this.fieldRules = {};

        if (fieldRules) {
            // Merge all rules
            for (const fr of fieldRules) {
                this.fieldRules[fr.fieldId] = this.fieldRules[fr.fieldId] ?? [];
                const oldRule = this.fieldRules[fr.fieldId];

                oldRule.push("Block");
                oldRule.push([fr.rules]);
            }
        }
    }

    /**
     * Evaluate fields runs over a set of fieldIDs and runs the rules on that field id. look below at _evaluateFieldRule to see more about how this works
     * @param fieldIds
     */
    public evaluateFields(fieldIds: number[]): number[] {
        const evalState: { [key: string]: IEvalStateValue } = {};

        for (const fieldId of fieldIds) {
            this._evaluateFieldRule(fieldId, evalState, false);
        }

        return this._evalStateToChangedFields(evalState);
    }

    public evaluateField(fieldId: number, valueLocked?: boolean, prevValue?: any): number[] {
        const evalState: { [key: string]: IEvalStateValue } = {};

        this._evaluateFieldRule(fieldId, evalState, valueLocked, prevValue);

        return this._evalStateToChangedFields(evalState);
    }

    /**
     *  the important thing to notice about this part is if you look below in _evaluateFieldRule, we create an evalState for every single field that we evaluated rules on, even if did not change.
     *  For work item save scenarios, this can be many more fields that even need to be notified, and it can be very unperformant to notify them all. So we only want to notify fields that have been changed by the
     *  save operation via rules or because those fields have actually changed.
     *  if you look at workItem.evaluate, which is what we call on save, in addition to running rules on the fields that have actually changed, we also run rules on a workItemTypes triggerList. What is the triggerList? Well if you look at
     *  WorkItemType definition in typescript, it gets triggerList from AdditionalWorkItemTypePropertiesFactory.cs, where triggerList is actually EVERY SINGLE FIELD THAT HAS A RULE ON IT!!!!

     * So, I'm pretty sure that this is yet another improvement that could be made. If we know which fields have actually changed, then we know which rules we need to run rules on. We don't need to run all rules every time.
     * But I'm not making that change right now because I'm not sure what that means, maybe more changes. But I think we should improve that part
     * @param evalState
     */
    private _evalStateToChangedFields(evalState: { [key: string]: IEvalStateValue }): number[] {
        const changedFields: number[] = [];
        for (const [key, value] of Object.entries(evalState)) {
            if (value.requiresControlUpdate) {
                changedFields.push(parseInt(key));
            }
        }
        return changedFields;
    }

    /**
     * This function evaluates all the rules for a certain field id, Each time we run the rules on a field, we create an evalState for that fieldId. The eval state contains a 'state', which is a boolean true false if the
     * field has been evaluated or not, a prevValue, and another boolean flag called requiresControlUpdate. We mark this flag as true if a fieldId has been changed by rules, or if the field itself has been changed, which happens on a
     * scenario where someone is just editing their work item in the form
     * @param fieldId
     * @param evalState
     * @param valueLocked
     * @param prevValue
     */
    private _evaluateFieldRule(fieldId: number, evalState: { [key: number]: IEvalStateValue }, valueLocked?: boolean, prevValue?: any) {
        const field = this.workItem.getFieldById(fieldId);
        if (!field) {
            return;
        }

        const fieldValue = this.workItem.getFieldValueById(fieldId);
        const fieldLists = field.lists;
        const fieldStatus = field.status;

        let fieldPreviouslyChangedByRule = false;

        // something to notice here is that the rule engine has depths of recursion where changing one field can cause trigger rule to change another field which causes trigger rule to change another field
        // what we really are doing here is building up a list of fields we know have changed so we can notify the controls of their updated state, so we want to make sure if a control is updated we update it even if rules
        // are run again on that field id. for more info go here: https://vsowiki.com/index.php?title=Work_Item_Tracking_Rules
        if (evalState[fieldId] && evalState[fieldId].requiresControlUpdate) {
            fieldPreviouslyChangedByRule = true;
        }

        evalState[fieldId] = {
            state: true,
            prevValue: prevValue === undefined ? fieldValue : prevValue,
            requiresControlUpdate: false || fieldPreviouslyChangedByRule
        };

        const fieldRuleEvaluator = new RuleEvaluator(
            fieldId,
            this.workItem,
            field,
            fieldValue,
            this.workItem.getFieldValueById(fieldId, true),
            valueLocked || false,
            evalState
        );
        fieldRuleEvaluator.evaluate(this.fieldRules[fieldId]);

        let evaluateTriggerFields: boolean;

        if (valueLocked) {
            // when value is locked field value is changed
            evaluateTriggerFields = true;
        } else {
            // only trigger fields if the field value, one of the lists  has changed, or the status has changed
            evaluateTriggerFields =
                !compareFieldValues(fieldValue, this.workItem.getFieldValueById(fieldId)) ||
                !RuleEvaluator.compareFieldLists(fieldLists, field.lists) ||
                (fieldStatus ^ field.status) !== 0;
        }

        // if the value has changed we need to notify this control of update (there can be multiple controls that have the same field id)
        if (evaluateTriggerFields) {
            evalState[fieldId].requiresControlUpdate = true;
        }

        if (evaluateTriggerFields && fieldRuleEvaluator.triggerFields) {
            fieldRuleEvaluator.triggerFields.forEach((triggerFieldId: number, i: number) => {
                const fieldEvalState = evalState[triggerFieldId];

                if (!fieldEvalState || !fieldEvalState.state) {
                    this._evaluateFieldRule(triggerFieldId, evalState, false);
                }
            });
        }

        evalState[fieldId].state = false;
    }
}
